import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '@ui/Input';
import Slider from '@ui/Slider';
import Checkbox from '@ui/Checkbox';
import Button from '@ui/Button';
import TextArea from '@ui/TextArea';
import { cx } from 'cva';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import AnimatedContent from './AnimatedContent';
import Tooltip from '@ui/Tooltip';
import FundraiserGivingLevels from './FundraiserGivingLevels';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { CampaignType } from '@/constants/postType.constants';
import useUserProfile from '@/hooks/useUserProfile';

const schemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Email must be a valid email id'),
});

const schemaCommon = yup.object().shape({
  // firstName: yup
  //   .string()
  //   .required('First name is required')
  //   .min(2, 'First name must be at least 2 characters')
  //   .max(50, 'First name must be at most 50 characters'),
  // lastName: yup
  //   .string()
  //   .required('Last name is required')
  //   .min(2, 'Last name must be at least 2 characters')
  //   .max(50, 'Last name must be at most 50 characters'),
  // email: yup
  //   .string()
  //   .required('Email is required')
  //   .email('Email must be a valid email id'),
  amount: yup
    .number()
    .required()
    .min(1, 'Minimum amount is $1')
    .typeError('Minimum amount is $1'),
  tip: yup.array().of(yup.number().min(0).max(100)).required(),
  billingPeriod: yup.string().required('Billing period is required'),
  doDedicate: yup.boolean().notRequired().nullable(),
  dedicatedToName: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to name is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToEmail: yup.string().when('doDedicate', {
    is: true,
    then: yup
      .string()
      .required('Dedicated to email is required')
      .email('Dedicated to email must be a valid email'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToMessage: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to message is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  isAnonymous: yup.boolean().notRequired().nullable(),
  shareNameAndEmail: yup.boolean().notRequired().nullable(),
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
});

function StripeDonationForm(props) {
  const { post, amount = '', onSubmit, isLoggedIn, nonProfit = {} } = props;

  const [frequencyTab, setFrequencyTab] = useState('One Time');
  const [amountTab, setAmountTab] = useState(null);
  const [matchedAmount, setMatchedAmount] = useState(null);
  const [showMatchAnimation, setShowMatchAnimation] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);
  const { campaignType } = post?.sourcePostData || {};

  const isLessThanLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const user = useUserProfile();

  const hasCompanyPayment =
    user?.activeCommunity?.companyPaymentGatewayConfig?.gatewayInfo?.paymentMethodId;

  const schema = isLoggedIn()
    ? schemaCommon
    : schemaPublic.concat(schemaCommon);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      amount: amount,
      tip: [30],
      // tip: [0],
      doDedicate: false,
      dedicatedToName: '',
      dedicatedToEmail: '',
      dedicatedToMessage: '',
      isAnonymous: false,
      shareNameAndEmail: true,
      agreeTerms: false,
      billingPeriod: 'one time',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
    setValue,
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
  } = form;

  const [amountVal, nekiTip, doDedicate] = watch([
    'amount',
    'tip',
    'doDedicate',
  ]);
  const donationAmount = Math.max(0, Number(amountVal));
  const tipAmount = Number(
    Math.max(0, (donationAmount * nekiTip) / 1000).toFixed(2),
  );

  const isRecurringDonation = () => frequencyTab !== 'One Time';

  const handleFrequencyTabChange = (tab) => {
    setFrequencyTab(tab);
    setValue('billingPeriod', `${tab.toLowerCase()}`);
  };

  const handleAmountTabChange = (tab) => {
    setAmountTab(tab);
    setValue('amount', `${tab}`);
  };

  const handleFormSubmit = (data) => {
    onSubmit(data, isRecurringDonation());
  };

  const handleUpdateCurrentLevel = (level) => {
    setCurrentLevel(level);
    setValue('amount', level.quantity);
  };

  const handleCompanyMatch = async () => {
    const { ok, data } = await fetchViaProxy(
      `/proxy/v1/users/me/donations/employer-matching?donationAmount=${amountVal}`,
    );
    if (ok && data?.data?.matchAmount) {
      setMatchedAmount(data.data.matchAmount);
      setShowMatchAnimation(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn() && amountVal) {
      handleCompanyMatch();
    }
  }, [amountVal]);

  useEffect(() => {
    if (showMatchAnimation) {
      setTimeout(() => {
        setShowMatchAnimation(false);
      }, 50);
    }
  }, [showMatchAnimation]);

  return (
    <div
      className={cx(
        'flex flex-1 flex-col justify-start',
        isLessThanMediumScreen && 'mb-20',
      )}>
      {/* <Typography variant="h1" className="mb-6">
        Make a Donation
      </Typography> */}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col justify-start gap-8 lg:flex-row lg:justify-between">
        <div className="flex w-full shrink-0 grow-0 flex-col gap-6 lg:w-6/12">
          <Typography variant="body1" color="textSecondary" className="">
            Donate your own funds to benefit the nonprofit
          </Typography>

          {!isLoggedIn() && (
            <>
              <div className="flex flex-col gap-4 lg:flex-row">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="First Name"
                      required
                      {...rest}
                      error={errors.firstName?.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Last Name"
                      required
                      {...rest}
                      error={errors.lastName?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name="email"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="Email Id"
                    required
                    {...rest}
                    error={errors.email?.message}
                  />
                )}
              />
            </>
          )}

          <div className={`${!isLoggedIn() ? 'mt-[-20px]' : ''}`}>
            {/* {isLoggedIn() && (
              <>
                <Typography
                  variant="h5"
                  // color="textSecondary"
                  className="uppercase">
                  Donation Frequency
                </Typography>
                <div className="mt-1 flex flex-wrap">
                  <div className="w-1/2 p-2">
                    <Button
                      onClick={() => handleFrequencyTabChange('One Time')}
                      variant={
                        frequencyTab === 'One Time' ? 'filled' : 'outlined'
                      }
                      color="transparent"
                      className={cx(
                        frequencyTab === 'One Time'
                          ? 'bg-secondary-light-1'
                          : 'border-1 border-solid border-secondary',
                        'w-full border border-secondary text-center font-dmsans text-14 font-medium text-secondary hover:bg-secondary-light-1',
                      )}>
                      One Time
                    </Button>
                  </div>
                  <div className="w-1/2 p-2">
                    <Button
                      onClick={() => handleFrequencyTabChange('Monthly')}
                      variant={
                        frequencyTab === 'Monthly' ? 'filled' : 'outlined'
                      }
                      color="transparent"
                      className={cx(
                        frequencyTab === 'Monthly'
                          ? 'bg-secondary-light-1'
                          : 'border-1 border-solid border-secondary',
                        'w-full border border-secondary text-center font-dmsans text-14 font-medium text-secondary hover:bg-secondary-light-1',
                      )}>
                      Monthly
                    </Button>
                  </div>
                  <div className="w-1/2 p-2">
                    <Button
                      onClick={() => handleFrequencyTabChange('Quarterly')}
                      variant={
                        frequencyTab === 'Quarterly' ? 'filled' : 'outlined'
                      }
                      color="transparent"
                      className={cx(
                        frequencyTab === 'Quarterly'
                          ? 'bg-secondary-light-1'
                          : 'border-1 border-solid border-secondary',
                        'w-full border border-secondary text-center font-dmsans text-14 font-medium text-secondary hover:bg-secondary-light-1',
                      )}>
                      Quarterly
                    </Button>
                  </div>
                  <div className="w-1/2 p-2">
                    <Button
                      onClick={() => handleFrequencyTabChange('Yearly')}
                      variant={
                        frequencyTab === 'Yearly' ? 'filled' : 'outlined'
                      }
                      color="transparent"
                      className={cx(
                        frequencyTab === 'Yearly'
                          ? 'bg-secondary-light-1'
                          : 'border-1 border-solid border-secondary',
                        'w-full border border-secondary text-center font-dmsans text-14 font-medium text-secondary hover:bg-secondary-light-1',
                      )}>
                      Yearly
                    </Button>
                  </div>
                </div>
                {frequencyTab === 'Monthly' && (
                  <div className="mt-2 flex flex-row gap-4">
                    <span>
                      <IoMdInformationCircleOutline />
                    </span>
                    <Typography variant="caption">
                      You will authorize Stripe to pay{' '}
                      {nonProfit?.name || 'NPO_NAME'} each month. You can change
                      or cancel a recurring donation at anytime in your Stripe
                      account settings.
                      <br />
                      Don&apos;t have Paypal? Signup easily while entering your
                      payment information.
                    </Typography>
                  </div>
                )}
              </>
            )} */}
            {/* <Typography
              variant="h5"
              // color="textSecondary"
              className={`mt-5 uppercase`}>
              Your Donation
            </Typography> */}
            {/* <div className="mt-1 flex gap-4">
              <Button
                onClick={() => handleAmountTabChange(10)}
                variant={amountTab === 10 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 10
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                $10
              </Button>
              <Button
                onClick={() => handleAmountTabChange(25)}
                variant={amountTab === 25 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 25
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                $25
              </Button>
              <Button
                onClick={() => handleAmountTabChange(50)}
                variant={amountTab === 50 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 50
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                $50
              </Button>
              <Button
                onClick={() => handleAmountTabChange(100)}
                variant={amountTab === 100 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 100
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                $100
              </Button>
            </div> */}
            {/* <div className="mt-1 flex gap-4">
              {!isLessThanLargeScreen &&
              post &&
              campaignType === CampaignType.FundRaise ? (
                <FundraiserGivingLevels
                  post={post}
                  noDonateButton
                  // className="w-fit flex-1 sm:w-full" // Adjusted width for small screens
                  // paddingClass="px-6 lg:px-4"
                  updateCurrentLevel={handleUpdateCurrentLevel}
                  // paddingClass="px-6 lg:px-4"
                  // size={isLessThanMediumScreen ? 'small' : 'auto'}
                />
              ) : (
                <>
                  <Button
                    onClick={() => handleAmountTabChange(10)}
                    variant={amountTab === 10 ? 'filled' : 'outlined'}
                    color="transparent"
                    className={cx(
                      amountTab === 10
                        ? 'bg-chips-secondary text-text-primary'
                        : 'bg-transparent text-secondary',
                      'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                    )}>
                    $10
                  </Button>
                  <Button
                    onClick={() => handleAmountTabChange(25)}
                    variant={amountTab === 25 ? 'filled' : 'outlined'}
                    color="transparent"
                    className={cx(
                      amountTab === 25
                        ? 'bg-chips-secondary text-text-primary'
                        : 'bg-transparent text-secondary',
                      'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                    )}>
                    $25
                  </Button>
                  <Button
                    onClick={() => handleAmountTabChange(50)}
                    variant={amountTab === 50 ? 'filled' : 'outlined'}
                    color="transparent"
                    className={cx(
                      amountTab === 50
                        ? 'bg-chips-secondary text-text-primary'
                        : 'bg-transparent text-secondary',
                      'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                    )}>
                    $50
                  </Button>
                  <Button
                    onClick={() => handleAmountTabChange(100)}
                    variant={amountTab === 100 ? 'filled' : 'outlined'}
                    color="transparent"
                    className={cx(
                      amountTab === 100
                        ? 'bg-chips-secondary text-text-primary'
                        : 'bg-transparent text-secondary',
                      'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                    )}>
                    $100
                  </Button>
                </>
              )}
            </div> */}
          </div>
          <Controller
            name="amount"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="DONATION AMOUNT"
                placeholder="Enter an Amount"
                required
                {...rest}
                adornment={
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="!font-medium">
                    $
                  </Typography>
                }
                error={errors.amount?.message}
              />
            )}
          />

          <Controller
            name="tip"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <div>
                <Typography variant="h5" className="mb-2.5 uppercase">
                  Neki Service Tip
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className="mb-8">
                  NEKI has a 0% platform fee for the donors and appreciates the
                  generosity of donors like you to support and enhance our
                  services.
                </Typography>
                <Slider {...rest} minLabel="0%" maxLabel="10%" />
              </div>
            )}
          />
        </div>
        <div className="flex flex-1 flex-col justify-between gap-2.5">
          <div className="flex flex-col gap-2.5">
            <Typography variant="h5" className="uppercase">
              Summary
            </Typography>

            <div className="flex">
              <Typography variant="body1" color="textSecondary">
                Your Donation
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className="ml-auto">
                ${donationAmount.toFixed(2)}
              </Typography>
            </div>
            <div className="flex border-b">
              <Typography variant="body1" color="textSecondary">
                NEKI Tip
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className="ml-auto">
                ${tipAmount.toFixed(2)}
              </Typography>
            </div>
            <div className="flex">
              <Typography variant="body1" color="textPrimary">
                Total
              </Typography>
              <Typography
                variant="body1"
                color="textPrimary"
                className="ml-auto">
                ${(tipAmount + donationAmount).toFixed(2)}
                {/* {donationAmount.toFixed(2)} */}
              </Typography>
            </div>

            {isLoggedIn() && hasCompanyPayment && matchedAmount && (
              <div className="relative flex">
                <Typography
                  variant="h5"
                  // color="textSecondary"
                  className="flex items-center justify-center">
                  Company Donation Match
                  <Tooltip
                    className="w-[150px]"
                    content="Congratulations! You've successfully utilized your company's donation matching program. Your company will add to your donation, amplifying your impact!">
                    <IoMdInformationCircleOutline className="ml-2 h-5 w-5" />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className="ml-auto">
                  ${(matchedAmount || 0.0).toFixed(2)}
                </Typography>
                <div className="absolute right-0 top-0">
                  <AnimatedContent
                    duration={1500}
                    show={showMatchAnimation}
                    content={`Your company will contribute $ ${(
                      matchedAmount || 0.0
                    ).toFixed(2)}`}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <Controller
                name="doDedicate"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-bold">
                Dedicate this donation to someone
              </Typography>
            </div>

            {doDedicate && (
              <div className="flex flex-col gap-4">
                <Controller
                  name="dedicatedToName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Name"
                      required
                      {...rest}
                      error={errors.dedicatedToName?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToEmail"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Email Id"
                      required
                      {...rest}
                      error={errors.dedicatedToEmail?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToMessage"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <TextArea
                      label="Message"
                      required
                      {...rest}
                      error={errors.dedicatedToMessage?.message}
                    />
                  )}
                />
              </div>
            )}

            {/* <div className="flex items-center gap-4">
              <Controller
                name="shareNameAndEmail"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-black">
                Share my name and email with the Charity
              </Typography>
            </div> */}

            <div className="flex items-center gap-4">
              <Controller
                name="isAnonymous"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-bold">
                Hide my name for this donation
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:gap-4">
            <Controller
              name="agreeTerms"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-4">
                    <Checkbox
                      {...rest}
                      error={errors.agreeTerms?.message}
                      className="border-text-disabled font-extrabold"
                    />
                    <Typography
                      variant="body2"
                      color="textDisabled"
                      className="font-bold">
                      I agree to the{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/terms-and-conditions"
                        className="text-primary hover:underline">
                        Terms of Service
                      </Typography>{' '}
                      and{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/privacy-policy"
                        className="text-primary hover:underline">
                        Privacy Policy
                      </Typography>
                    </Typography>
                  </div>
                  <Typography
                    variant="body2"
                    color="error"
                    className={!errors.agreeTerms && 'hidden'}>
                    Please agree to proceed
                  </Typography>
                </div>
              )}
            />

            <Button
              type="submit"
              variant="filled"
              color="primary"
              className={cx(
                'flex w-full items-center justify-center',
                doDedicate && 'mb-4',
              )}>
              Donate
            </Button>
            <Typography
              variant="body1"
              color="textSecondary"
              className="flex items-center justify-center font-bold">
              Powered by{' '}
              <img src="/assets/icons/stripe-logo.svg" alt="stripe-logo" />
            </Typography>
          </div>
        </div>
      </form>

      <Typography
        variant="caption"
        color="textSecondary"
        className="mb-2 mt-8 pb-14 text-justify lg:pb-0">
        Your donation will be processed by Stripe. The donation will be going
        directly to this IRS-registered 501(c)(3) charity, subject to its terms.
        The charity receives the donation, which is typically tax deductible,
        minus payment processor&apos;s processing fees of 2.9% + $0.30. The
        donation is processed in real time by Stripe and the funds are available
        directly in your nonprofit&apos;s stripe account immediately.
      </Typography>
    </div>
  );
}

export default StripeDonationForm;
